import { z } from 'zod';

import type { MatchesSchema } from '@/shared/api/contract';

/**
 * Kontrakt odpowiedzi API, sprawdzany w czasie działania.
 *
 * Nie ufamy kształtowi odpowiedzi na słowo. `zod` łapie rozjazd kontraktu
 * w miejscu, w którym powstał — a nie trzy komponenty dalej, jako
 * „cannot read property of undefined". Przy module wdrażanym niezależnie
 * od frontu (decyzja 69) to nie jest paranoja.
 */

export const teamSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Symbol zespołu dla pliku importu — dla sesji nieistotny, ale kontrakt jest jeden. */
  key: z.string().nullable(),
  memberCount: z.number(),
  /** Za ile elementów zespół odpowiada wprost — dla sesji nieistotne, ale kontrakt jest jeden. */
  ownedElementCount: z.number(),
});

export const currentUserSchema = z.object({
  id: z.number(),
  login: z.string(),
  displayName: z.string(),
  roles: z.array(z.string()),
  teams: z.array(teamSummarySchema),
});

export type TeamSummary = z.infer<typeof teamSummarySchema>;
export type CurrentUser = z.infer<typeof currentUserSchema>;

export interface LoginInput {
  login: string;
  password: string;
}

export interface RegistrationInput {
  login: string;
  displayName: string;
  password: string;
}

/**
 * Role systemowe — nazwy **muszą** się zgadzać z `DydationRoles` po stronie API (decyzja 81).
 *
 * ⚠ **Tego NIE pilnuje kontrakt OpenAPI:** role jadą po drucie jako zwykłe napisy w tablicy
 * `roles`, więc literówka tutaj niczego nie zepsuje w typowaniu — po prostu **po cichu odbierze
 * uprawnienia**. Jedyna obrona to trzymanie ich w jednym miejscu i nieużywanie literałów w kodzie.
 *
 * ⭐ Role są **płaskimi zbiorami**: konto może mieć kilka naraz, a admin **nie dziedziczy**
 * uprawnień architekta ani lidera — dokłada go dopiero polityka (patrz `canSee`).
 */
export const ROLES = {
  admin: 'admin',
  architect: 'architect',
  leader: 'leader',
  user: 'user',
} as const;

export type DydationRole = (typeof ROLES)[keyof typeof ROLES];

/** Zostaje osobno, bo `isAdmin(user)` czyta się lepiej niż `hasRole(user, ROLES.admin)`. */
export const ADMIN_ROLE = ROLES.admin;

export function hasRole(user: CurrentUser | null | undefined, role: string): boolean {
  return user?.roles.includes(role) ?? false;
}

export function hasAnyRole(
  user: CurrentUser | null | undefined,
  roles: readonly string[],
): boolean {
  return roles.some((role) => hasRole(user, role));
}

export function isAdmin(user: CurrentUser | null | undefined): boolean {
  return hasRole(user, ROLES.admin);
}

/**
 * Czy pokazać rzecz chronioną tymi rolami — **z adminem wliczonym**. Brak listy = dla każdego.
 *
 * ⭐ **To jest lustro serwerowego `WithAdmin`** (`IdentityExtensions`): tam każda polityka poza
 * `AdminOnly` przepuszcza także admina. Gdyby front tej reguły nie powtarzał, administrator bez
 * roli architekta **nie zobaczyłby sekcji, na którą serwer by go wpuścił** — czyli ukrywalibyśmy
 * drzwi, które są otwarte. Objaw byłby przy tym mylący: „u mnie tego nie ma" zamiast „brak dostępu".
 *
 * ⚠ To **wygoda, nie kontrola dostępu**. Ukrywanie sekcji nie zastępuje polityki na serwerze.
 */
export function canSee(
  user: CurrentUser | null | undefined,
  roles?: readonly string[],
): boolean {
  if (roles === undefined || roles.length === 0) {
    return true;
  }

  return hasAnyRole(user, [...roles, ROLES.admin]);
}

// ── Spinacz z kontraktem API (decyzja 74) ───────────────────────────────────
//
// Moduł powstał w fazie C1, czyli PRZED decyzją 74, i jako jedyny nie miał tych par.
// To była realna dziura, nie formalność: przez logowanie przechodzi każdy user, więc
// rozjazd akurat tutaj wywraca aplikację na wejściu, zanim ktokolwiek zobaczy ekran.
//
// ⚠ Nazwy po drugiej stronie NIE są lustrzane — API mówi `CurrentUserResponse`,
// `LoginRequest`, `RegistrationRequest`. Nazwy własne frontu zostają, bo to nasz język
// dziedziny; para wiąże je z dokumentem jawnie, zamiast wymuszać wspólne nazewnictwo.

/** Każda pozycja to jedna para „nasz typ ↔ schemat z dokumentu OpenAPI". */
export const authContract = {
  CurrentUser: true satisfies MatchesSchema<CurrentUser, 'CurrentUserResponse'>,
  TeamSummary: true satisfies MatchesSchema<TeamSummary, 'TeamSummary'>,
  LoginInput: true satisfies MatchesSchema<LoginInput, 'LoginRequest'>,
  RegistrationInput: true satisfies MatchesSchema<RegistrationInput, 'RegistrationRequest'>,
} as const;
